import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Toolbar,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  Grid,
  Button,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../../utils/Api';
import { useNavigate } from 'react-router-dom';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CheckCircleRounded,
  AccessTimeRounded,
  CancelRounded,
  DoneAllRounded,
  InventoryOutlined,
  CalendarMonthOutlined,
  AssessmentOutlined,
  RefreshRounded,
  FilterAltRounded,
  DeleteForeverOutlined,
  CheckCircleOutlineOutlined,
} from '@mui/icons-material';

const StockRequestsList = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const outletData = JSON.parse(localStorage.getItem('outletData') || '{}');

  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteReq, setSelectedDeleteReq] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const normalizeStatus = (status = '') => {
    const s = String(status).trim().toUpperCase();

    if (s === 'ACCEPTED') return 'ACCEPTED';
    if (s === 'COMPLETED' || s === 'READY-TO-PICK' || s === 'IN-PRODUCTION') return 'COMPLETED';
    // if (s === 'COMPLETED') return 'COMPLETED';
    if (s === 'RECEIVED') return 'RECEIVED';
    if (s === 'RECIEVED') return 'RECEIVED';
    if (s === 'CLOSED') return 'CLOSED';
    if (s === 'REJECTED') return 'REJECTED';
    if (s === 'CANCELLED') return 'REJECTED';

    return 'PENDING';
  };

  const fetchRequests = async (range = dateRange) => {
    try {
      setLoading(true);

      const params = {
        outlet: outletData?._id,
      };

      if (range.from) params.startDate = range.from;
      if (range.to) params.endDate = range.to;
      // page=1&limit=20&outlet=66ea74e12f6e11c3f2527eb7&startDate=2026-05-01&endDate=2026-05-14
      
      const response = await axios.get(`${API.BASE_URL}stock-requisition`, {
      // const response = await axios.get(`${API.BASE_URL}stock-requisition/get-outlet-requests`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const list = response?.data?.data?.data || [];
      setRequests(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error ||
        'Failed to fetch material request indents.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const formatDate = (value) => {
    if (!value) return '-';

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';

    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getStatusConfig = (status = '') => {
    const s = normalizeStatus(status);

    switch (s) {
      case 'PENDING':
        return {
          label: 'PENDING',
          bg: '#fff7e6',
          color: '#b26a00',
          border: '#ffd591',
          accent: '#f59e0b',
          softBg: 'linear-gradient(135deg, #fffaf1 0%, #ffffff 100%)',
          icon: <AccessTimeRounded sx={{ fontSize: 15 }} />,
        };

      case 'ACCEPTED':
        return {
          label: 'ACCEPTED',
          bg: '#e8f4ff',
          color: '#0b5cab',
          border: '#91caff',
          accent: '#0288d1',
          softBg: 'linear-gradient(135deg, #f3f9ff 0%, #ffffff 100%)',
          icon: <CheckCircleRounded sx={{ fontSize: 15 }} />,
        };

      case 'COMPLETED':
        return {
          label: 'DISPATCHED',
          bg: '#f3e8ff',
          color: '#6b21a8',
          border: '#d8b4fe',
          accent: '#9333ea',
          softBg: 'linear-gradient(135deg, #faf5ff 0%, #ffffff 100%)',
          icon: <DoneAllRounded sx={{ fontSize: 15 }} />,
        };

      case 'RECEIVED':
        return {
          label: 'RECEIVED',
          bg: '#e9f7ef',
          color: '#1e7e34',
          border: '#95d5b2',
          accent: '#2e7d32',
          softBg: 'linear-gradient(135deg, #f3fff8 0%, #ffffff 100%)',
          icon: <InventoryOutlined sx={{ fontSize: 15 }} />,
        };

      case 'CLOSED':
        return {
          label: 'CLOSED',
          bg: '#eef2f7',
          color: '#334155',
          border: '#cbd5f5',
          accent: '#64748b',
          softBg: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          icon: <CheckCircleOutlineOutlined sx={{ fontSize: 15 }} />,
        };

      case 'REJECTED':
        return {
          label: 'REJECTED',
          bg: '#fff1f0',
          color: '#c62828',
          border: '#ffa39e',
          accent: '#d32f2f',
          softBg: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
          icon: <CancelRounded sx={{ fontSize: 15 }} />,
        };

      default:
        return {
          label: 'ALL',
          bg: '#eef2ff',
          color: '#003049',
          border: '#c7d2fe',
          accent: '#0f4c75',
          softBg: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          icon: <AssessmentOutlined sx={{ fontSize: 15 }} />,
        };
    }
  };

  const getReceivedQty = (item) => item.totalReceivedQuantity ?? 0;

  const getTotalRequestedQty = (items = []) => items.reduce((total, item) => total + Number(item?.requestedQuantity || 0), 0);

  const getTotalReceivedQty = (items = []) => items.reduce((total, item) => total + Number(getReceivedQty(item) || 0), 0);

  const closedCount = requests.filter(
    (r) => normalizeStatus(r.status) === 'CLOSED'
  ).length;

  const canDelete = (status) => normalizeStatus(status) === 'PENDING';

  const totalRequests = requests.length;

  const pendingCount = requests.filter((r) => normalizeStatus(r.status) === 'PENDING').length;
  const acceptedCount = requests.filter((r) => normalizeStatus(r.status) === 'ACCEPTED').length;
  const completedCount = requests.filter((r) => normalizeStatus(r.status) === 'COMPLETED').length;
  const receivedCount = requests.filter((r) => normalizeStatus(r.status) === 'RECEIVED').length;
  const rejectedCount = requests.filter((r) => normalizeStatus(r.status) === 'REJECTED').length;

  const filteredRequests = useMemo(() => {
    if (selectedStatus === 'ALL') return requests;

    return requests.filter(
      (req) => normalizeStatus(req.status) === selectedStatus
    );
  }, [requests, selectedStatus]);

  const activeConfig = selectedStatus === 'ALL' ? getStatusConfig('ALL') : getStatusConfig(selectedStatus);

  const openDeleteModal = (event, req) => {
    event.stopPropagation();
    setSelectedDeleteReq(req);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setSelectedDeleteReq(null);
  };

  const handleDeleteStockRequisition = async () => {
    if (!selectedDeleteReq?._id) return;

    try {
      setDeleteLoading(true);

      await axios.delete(`${API.BASE_URL}stock-requisition/delete`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          _id: selectedDeleteReq._id,
        },
      });

      toast.success('Stock requisition deleted successfully.');

      setRequests((prev) =>
        prev.filter((item) => item._id !== selectedDeleteReq._id)
      );

      closeDeleteModal();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to delete stock requisition.'
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f3f4f7 0%, #eef2f7 100%)',
      }}
    >
      <Toolbar />

      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          pb: 5,
          pt: { xs: 2.5, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 2.2, md: 3 },
            borderRadius: 4,
            background: `
              radial-gradient(circle at top right, rgba(255,255,255,0.20), transparent 34%),
              linear-gradient(135deg, #0f4c75 0%, #003049 100%)
            `,
            color: '#fff',
            boxShadow: '0 18px 45px rgba(0, 48, 73, 0.22)',
          }}
        >
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Stack direction="row" spacing={1.6} alignItems="center">
                <IconButton
                  onClick={() => navigate('/managerDashboard')}
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.13)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.24)',
                    },
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>

                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.18)',
                  }}
                >
                  <Inventory2OutlinedIcon sx={{ fontSize: 30 }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 21, sm: 26 },
                      fontWeight: 900,
                      lineHeight: 1.15,
                    }}
                  >
                    Stock Requisition List
                  </Typography>

                  <Typography sx={{ fontSize: 13.5, opacity: 0.86, mt: 0.5 }}>
                    Track stock requests, approvals, completion and received status
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                <Chip
                  label={`${filteredRequests.length} Showing / ${totalRequests} Total`}
                  sx={{
                    height: 36,
                    fontSize: 12.5,
                    fontWeight: 900,
                    bgcolor: '#fff',
                    color: '#003049',
                    border: '1px solid rgba(255,255,255,0.45)',
                  }}
                />

                <Button
                  startIcon={<RefreshRounded />}
                  onClick={() => fetchRequests()}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    borderRadius: 2.5,
                    px: 2,
                    bgcolor: 'rgba(255,255,255,0.14)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.22)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.22)',
                    },
                  }}
                >
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 1.6, md: 2 },
            borderRadius: 4,
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2.3,
                    bgcolor: '#eef6fb',
                    color: '#0f4c75',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FilterAltRounded sx={{ fontSize: 20 }} />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>
                    Filters
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    Select date range and click status cards to filter requests
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                <TextField
                  type="date"
                  size="small"
                  label="From Date"
                  value={dateRange.from}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: { xs: '100%', sm: 155 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: '#f8fafc',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 13,
                      fontWeight: 700,
                    },
                  }}
                />

                <TextField
                  type="date"
                  size="small"
                  label="To Date"
                  value={dateRange.to}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: { xs: '100%', sm: 155 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor: '#f8fafc',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 13,
                      fontWeight: 700,
                    },
                  }}
                />

                <Button
                  onClick={() => fetchRequests()}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    borderRadius: 2.5,
                    px: 2.2,
                    bgcolor: '#0f4c75',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#003049',
                    },
                  }}
                >
                  Apply
                </Button>

                <Button
                  onClick={() => {
                    const emptyRange = { from: '', to: '' };
                    setDateRange(emptyRange);
                    fetchRequests(emptyRange);
                  }}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 900,
                    borderRadius: 2.5,
                    px: 2.2,
                    color: '#334155',
                    border: '1px solid #e5e7eb',
                    bgcolor: '#fff',
                  }}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" justifyContent={'space-around'} spacing={1} flexWrap="wrap" useFlexGap>
              {[
                { title: 'All', status: 'ALL', count: totalRequests },
                { title: 'Pending', status: 'PENDING', count: pendingCount },
                { title: 'Accepted', status: 'ACCEPTED', count: acceptedCount },
                { title: 'Dispatched', status: 'COMPLETED', count: completedCount },
                { title: 'Received', status: 'RECEIVED', count: receivedCount },
                { title: 'Closed', status: 'CLOSED', count: closedCount },
                { title: 'Rejected', status: 'REJECTED', count: rejectedCount },
              ].map((item) => {
                const config = getStatusConfig(item.status);
                const isActive = selectedStatus === item.status;

                return (
                  <Chip
                    key={item.status}
                    icon={config.icon}
                    label={`${item.title} (${item.count})`}
                    onClick={() => {
                      if (item.status === 'ALL') {
                        setSelectedStatus('ALL');
                      } else {
                        setSelectedStatus((prev) =>
                          prev === item.status ? 'ALL' : item.status
                        );
                      }
                    }}
                    sx={{
                      height: 38,
                      px: 0.8,
                      borderRadius: 2.5,
                      fontSize: 12.5,
                      fontWeight: 900,
                      cursor: 'pointer',
                      bgcolor: isActive ? config.bg : '#fff',
                      color: isActive ? config.color : '#334155',
                      border: `1px solid ${isActive ? config.accent : '#e5e7eb'}`,
                      boxShadow: isActive
                        ? `0 8px 18px ${config.accent}22`
                        : '0 4px 10px rgba(15, 23, 42, 0.04)',
                      '& .MuiChip-icon': {
                        color: isActive ? config.color : '#64748b',
                        fontSize: 18,
                      },
                      '&:hover': {
                        bgcolor: config.bg,
                        color: config.color,
                        borderColor: config.border,
                        '& .MuiChip-icon': {
                          color: config.color,
                        },
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={(theme) => ({
            borderRadius: 4,
            p: { xs: 2, sm: 3 },
            minHeight: '60vh',
            border: `1px solid ${theme.palette.divider}`,
            background: '#fff',
            boxShadow: '0 14px 35px rgba(15, 23, 42, 0.08)',
          })}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: '#17202a' }}>
                Recent Requisitions
              </Typography>

              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.3 }}>
                Showing{' '}
                <Box component="span" sx={{ fontWeight: 900, color: activeConfig.color }}>
                  {selectedStatus === 'ALL' ? 'all' : activeConfig.label.toLowerCase()}
                </Box>{' '}
                stock requests
              </Typography>
            </Box>

            <Chip
              icon={activeConfig.icon}
              label={`${selectedStatus === 'ALL' ? 'All Status' : activeConfig.label}: ${filteredRequests.length}`}
              sx={{
                height: 34,
                fontSize: 12,
                fontWeight: 900,
                bgcolor: activeConfig.bg,
                color: activeConfig.color,
                border: `1px solid ${activeConfig.border}`,
                '& .MuiChip-icon': {
                  color: activeConfig.color,
                },
              }}
            />
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          {loading ? (
            <Box
              sx={{
                height: '46vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <CircularProgress size={32} />
              <Typography sx={{ mt: 1.8, fontSize: 14, fontWeight: 700 }}>
                Fetching material request indents...
              </Typography>
            </Box>
          ) : filteredRequests.length === 0 ? (
            <Box
              sx={{
                height: '46vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 74,
                  height: 74,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(15, 76, 117, 0.08)',
                  color: '#0f4c75',
                  mb: 1.8,
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 36 }} />
              </Box>

              <Typography fontWeight={900} sx={{ fontSize: 16, color: '#17202a' }}>
                No {selectedStatus === 'ALL' ? '' : selectedStatus.toLowerCase()} requests found
              </Typography>

              <Typography sx={{ mt: 0.8, maxWidth: 390, fontSize: 13 }}>
                Try changing the selected status card or date range.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.6}>
              {filteredRequests
                .slice()
                .map((req) => {
                  const items = req.items || [];
                  const statusConfig = getStatusConfig(req.status);
                  const normalizedStatus = normalizeStatus(req.status);

                  const showReceivedQty = normalizedStatus === 'RECEIVED';

                  const totalRequestedQty = getTotalRequestedQty(items);
                  const totalReceivedQty = getTotalReceivedQty(items);

                  const firstItemName =
                    items?.[0]?.rawMaterial?.name ||
                    items?.[0]?.material?.name ||
                    null;

                  return (
                    <Paper
                      key={req._id}
                      elevation={0}
                      onClick={() =>
                        navigate('/stock-requistion-details', {
                          state: { indent: req },
                        })
                      }
                      sx={(theme) => ({
                        borderRadius: 3.5,
                        border: `1px solid ${theme.palette.divider}`,
                        background: statusConfig.softBg,
                        p: { xs: 1.8, sm: 2.2 },
                        pl: { xs: 2.1, sm: 2.8 },
                        cursor: 'pointer',
                        transition: 'all 0.22s ease',
                        boxShadow: '0 5px 16px rgba(15, 23, 42, 0.045)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 14,
                          bottom: 14,
                          width: 4,
                          borderRadius: 8,
                          background: statusConfig.accent,
                        },
                        '&:hover': {
                          boxShadow: '0 16px 34px rgba(15, 23, 42, 0.10)',
                          borderColor: statusConfig.border,
                          transform: 'translateY(-2px)',
                        },
                        '&:hover .view-arrow': {
                          transform: 'translateX(4px)',
                        },
                      })}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={1.8}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mb: 1 }}
                          >
                            <Typography
                              fontWeight={900}
                              sx={{
                                fontSize: 17,
                                color: '#0f172a',
                                letterSpacing: '-0.01em',
                              }}
                            >
                              {req.indentNumber || '-'}
                            </Typography>

                            <Chip
                              size="small"
                              icon={statusConfig.icon}
                              label={statusConfig.label}
                              sx={{
                                height: 28,
                                fontSize: 11,
                                textTransform: 'uppercase',
                                fontWeight: 900,
                                letterSpacing: 0.35,
                                bgcolor: statusConfig.bg,
                                color: statusConfig.color,
                                border: `1px solid ${statusConfig.border}`,
                                '& .MuiChip-icon': {
                                  ml: 0.8,
                                  color: statusConfig.color,
                                },
                              }}
                            />
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <CalendarMonthOutlined
                              sx={{ fontSize: 16, color: 'text.secondary' }}
                            />

                            <Typography
                              color="text.secondary"
                              sx={{ fontSize: 13, fontWeight: 700 }}
                            >
                              Requested on{' '}
                              <Box component="span" sx={{ color: '#34495e', fontWeight: 900 }}>
                                {formatDate(req.createdAt)}
                              </Box>
                            </Typography>
                          </Stack>

                          {firstItemName && (
                            <Typography
                              sx={{
                                mt: 1,
                                fontSize: 13,
                                color: 'text.secondary',
                              }}
                            >
                              Includes:{' '}
                              <Box component="span" sx={{ color: '#17202a', fontWeight: 800 }}>
                                {firstItemName}
                              </Box>
                              {items.length > 1 ? ` + ${items.length - 1} more` : ''}
                            </Typography>
                          )}

                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 1.3 }}
                          >
                            <Chip
                              size="small"
                              label={`${items.length} item${items.length !== 1 ? 's' : ''}`}
                              sx={{
                                height: 28,
                                fontSize: 11.5,
                                fontWeight: 900,
                                bgcolor: '#fff',
                                color: '#334155',
                                border: '1px solid #e5e7eb',
                              }}
                            />

                            <Chip
                              size="small"
                              label={`Requested Qty: ${totalRequestedQty}`}
                              sx={{
                                height: 28,
                                fontSize: 11.5,
                                fontWeight: 900,
                                bgcolor: '#f8fafc',
                                color: '#334155',
                                border: '1px solid #e5e7eb',
                              }}
                            />

                            {showReceivedQty && (
                              <Chip
                                size="small"
                                label={`Received Qty: ${totalReceivedQty}`}
                                sx={{
                                  height: 28,
                                  fontSize: 11.5,
                                  fontWeight: 900,
                                  bgcolor: '#f8fafc',
                                  color: '#334155',
                                  border: '1px solid #e5e7eb',
                                }}
                              />
                            )}

                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            width: { xs: '100%', md: 'auto' },
                            display: 'flex',
                            justifyContent: { xs: 'space-between', md: 'flex-end' },
                            alignItems: 'center',
                            gap: 1,
                            pt: { xs: 0.5, md: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: '#0f4c75',
                              fontWeight: 900,
                              px: 1.2,
                              py: 0.7,
                              borderRadius: 2.2,
                              backgroundColor: 'rgba(15, 76, 117, 0.08)',
                            }}
                          >
                            <Typography sx={{ fontSize: 12.8, fontWeight: 900 }}>
                              View details
                            </Typography>

                            <KeyboardArrowRight
                              className="view-arrow"
                              sx={{
                                fontSize: 19,
                                transition: 'transform 0.2s ease',
                              }}
                            />
                          </Box>
                          {normalizedStatus === 'PENDING' && (
                            <IconButton
                              onClick={(event) => openDeleteModal(event, req)}
                              sx={{
                                color: '#d32f2f',
                                bgcolor: 'rgba(211, 47, 47, 0.08)',
                                border: '1px solid rgba(211, 47, 47, 0.18)',
                                '&:hover': {
                                  bgcolor: 'rgba(211, 47, 47, 0.14)',
                                },
                              }}
                            >
                              <DeleteForeverOutlined />
                            </IconButton>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
            </Stack>
          )}
        </Paper>
        <Dialog
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
            },
          }}
        >
          {/* HEADER */}
          <DialogTitle
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
              color: '#fff',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.5,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DeleteForeverOutlined />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 900 }}>
                  Delete Request
                </Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                  This action cannot be undone
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>

          {/* CONTENT */}
          <DialogContent sx={{ p: 2.5 }}>
            <Typography
              sx={{
                fontSize: 14,
                color: '#374151',
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to delete this stock requisition?
            </Typography>

            {selectedDeleteReq?.indentNumber && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#fef2f2',
                  border: '1px solid #fecaca',
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  Indent No:
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 900, color: '#b91c1c' }}>
                  {selectedDeleteReq.indentNumber}
                </Typography>
              </Box>
            )}

            <Typography
              sx={{
                mt: 2,
                fontSize: 12.5,
                color: '#6b7280',
              }}
            >
              Only pending requests can be deleted. This will permanently remove the record.
            </Typography>
          </DialogContent>

          {/* ACTIONS */}
          <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
            <Button
              onClick={closeDeleteModal}
              disabled={deleteLoading}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                color: '#374151',
                borderRadius: 2,
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleDeleteStockRequisition}
              startIcon={
                deleteLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteForeverOutlined />
                )
              }
              disabled={deleteLoading}
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: 2,
                bgcolor: '#ef4444',
                '&:hover': {
                  bgcolor: '#b91c1c',
                },
              }}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default StockRequestsList;